/**
 * The screenshot that rides along with a capture (日本-100).
 *
 * The tray agent grabs the window you were reading from, alongside the region it OCR'd, and gets it
 * into the sentence's image slot — so a card mined from that sentence inherits the picture without
 * anyone pasting it by hand.
 *
 * **It arrives one of two ways, and the difference is not cosmetic.**
 *
 * - **A tab is already open.** The agent pushes the text over its loopback SSE stream *immediately*,
 *   carrying only a `captureId` — the upload has not happened yet. The sentence appears at full
 *   speed with its slot pending, and the **server** publishes `mediaStored` when the bytes land.
 * - **No tab is open.** The agent opens one with `/?q=…`, and that URL has to name a file that
 *   exists, because there is nobody to send a follow-up event to. So that path carries a finished
 *   filename and the slot is filled on arrival. It is the slow path anyway (a cold browser, maybe a
 *   sign-in), so the upload hides inside a wait that was already happening.
 *
 * **Why a `captureId` and not just the filename.** Anki's media store may hand back a different name
 * than it was asked for, and — the branch that catches people — it does so for a name containing
 * *any uppercase character*, with no collision involved at all. So the only stable handle on an
 * upload that has not finished is a guid the agent minted before starting it.
 *
 * Pure, because `npm test` has no DOM. Everything here is a parsing or validation rule, which is
 * where the failures live: a slot that silently defaults, or a name interpolated into markup.
 */

import { CAPTURE_SCHEME } from "@/lib/note";

import type { SlotKey } from "./ImageSlots";

/** A screenshot still uploading. The slot shows `storing…` until `mediaStored` names the file. */
export interface PendingCapture {
  captureId: string;
  slot: SlotKey;
}

/**
 * A screenshot the upload has already finished with, named in a deep link.
 *
 * `filename` is whatever `/media/store` answered — since capture staging that is a `capture:<id>`
 * reference rather than a name in `col.media`. Both go into the slot as a field value and both
 * come back out through `mediaUrl`, which is the only place that has to tell them apart.
 */
export interface StoredCapture {
  filename: string;
  slot: SlotKey;
}

/**
 * The two slot names, as an exhaustive check rather than a cast.
 *
 * **No default.** A malformed slot resolving to `image` would overwrite the screenshot you actually
 * wanted there — strictly worse than the picture not arriving, because you lose something.
 */
function asSlot(value: unknown): SlotKey | null {
  return value === "image" || value === "extraImage" ? value : null;
}

/** `Guid.ToString("N")` — 32 lowercase hex. Validated because it reaches a lookup key. */
const CAPTURE_ID = /^[0-9a-f]{32}$/;

/**
 * A media filename we are willing to interpolate into an `<img src>`.
 *
 * The server basenames on write, so the path separators are the client half of the same rule.
 *
 * **The rest of the excluded set is the load-bearing part, not the extension check.** This value is
 * interpolated *unescaped* by `wrapImageValue`, and the result is stored in the card's `Image`
 * field — which Anki renders as HTML on every device the collection syncs to. A name containing `"`
 * closes the attribute and turns everything after it into markup, on a card that then syncs to the
 * phone. So quotes, angle brackets, whitespace and control characters are out.
 *
 * Rejecting rather than sanitising: Anki's own `normalize_filename` deletes most of these on write,
 * so a name carrying one cannot have come from a file this app stored, and quietly cleaning it up
 * would hide that.
 */
const FILENAME = /^[^/\\"'<>\s]+\.[A-Za-z0-9]{1,10}$/;

function validFilename(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const name = value.trim();
  if (!name || name.includes("..") || !FILENAME.test(name)) return null;
  // Control characters, by code point rather than as a regex range. `\s` above already covers the
  // ones anybody types; this covers the rest, and doing it here keeps literal control bytes out of
  // this source file — which is what writing the range inline actually produces.
  for (const character of name) {
    const code = character.codePointAt(0) ?? 0;
    if (code < 0x20 || code === 0x7f) return null;
  }
  return name;
}

/** The pending screenshot named in an SSE `search` frame, or null. */
export function captureFromPush(data: unknown): PendingCapture | null {
  if (typeof data !== "object" || data === null) return null;
  const frame = data as { captureId?: unknown; slot?: unknown };
  const slot = asSlot(frame.slot);
  const id = typeof frame.captureId === "string" ? frame.captureId : "";
  // Both or neither: half a reference is a slot that would wait forever, or a file with nowhere
  // to go.
  if (!slot || !CAPTURE_ID.test(id)) return null;
  return { captureId: id, slot };
}

/**
 * A reference the deep link may carry: a media filename, **or a staged capture**.
 *
 * Since 日本-301 a tray upload is staged rather than stored — `/media/store` answers
 * `capture:<id>`, not a file in `col.media` — and that reference has no extension, so
 * `validFilename` rejects it outright. Left that way the deep-link path (no Read tab open, the
 * ONLY path that waits on the upload) silently dropped every screenshot: the sentence arrived, the
 * slot stayed empty, and nothing anywhere said why.
 *
 * The staged form is validated on the id rather than on the filename rules, for the same reason
 * `captureFromPush` does — it is a guid, and it is the whole value. It still cannot carry a quote
 * or an angle bracket, so the `wrapImageValue` hazard those rules exist for is closed either way.
 */
function validReference(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const raw = value.trim();
  if (raw.startsWith(CAPTURE_SCHEME)) {
    const id = raw.slice(CAPTURE_SCHEME.length);
    return CAPTURE_ID.test(id) ? raw : null;
  }
  return validFilename(raw);
}

/** The stored or staged screenshot named in a deep link's query string, or null. */
export function captureFromQuery(search: string): StoredCapture | null {
  const params = new URLSearchParams(search);
  const slot = asSlot(params.get("slot"));
  const filename = validReference(params.get("image"));
  if (!slot || !filename) return null;
  return { filename, slot };
}
